import type { ReactNode } from "react";
import {
  IconAnalytics,
  IconBell,
  IconCalendar,
  IconChannels,
  IconClock,
  IconContent,
  IconHelp,
  IconOps,
  IconPlus,
  IconSettings,
  IconSidebar,
} from "../lib/icons";

/** The primary views the sidebar switches between. Each is a real page rendered
 * in the workspace area (Settings + Create post included — full in-frame pages,
 * not slide-overs). `ops` is the owner-only Command Center. */
export type NavKey =
  | "calendar"
  | "planner"
  | "compose"
  | "connections"
  | "analytics"
  | "activity"
  | "notifications"
  | "ops"
  | "settings";

const NAV_ITEMS: { key: NavKey; label: string; Icon: typeof IconCalendar }[] = [
  { key: "calendar", label: "Dashboard", Icon: IconContent },
  { key: "planner", label: "Planner", Icon: IconCalendar },
  { key: "compose", label: "Create post", Icon: IconPlus },
  { key: "connections", label: "Connections", Icon: IconChannels },
  { key: "analytics", label: "Analytics", Icon: IconAnalytics },
  { key: "activity", label: "Activity", Icon: IconClock },
  { key: "notifications", label: "Notifications", Icon: IconBell },
];

/**
 * The constant charcoal left rail: brand, primary nav (with a red active
 * indicator), Settings pinned lower, and the workspace switcher pinned at the
 * very bottom. Collapses to icons via `collapsed`.
 */
export function Sidebar({
  nav,
  onNav,
  collapsed,
  onToggleCollapse,
  workspaceSlot,
  notifUnread = 0,
  isOwner = false,
  onHelp,
}: {
  nav: NavKey;
  onNav: (key: NavKey) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  workspaceSlot: ReactNode;
  /** Unread notification count — shown as a badge on the Notifications item. */
  notifUnread?: number;
  /** Owner-only: shows the Command Center item (the endpoint enforces the gate). */
  isOwner?: boolean;
  /** Open the Help & FAQ slide-over. */
  onHelp?: () => void;
}) {
  return (
    <aside className="cc-sidebar" aria-label="Primary">
      <div className="cc-brand">
        <img className="cc-brand-mark" src="/logo.svg" alt="" width={30} height={30} />
        <span className="cc-brand-word">Broadside</span>
      </div>

      <nav className="cc-nav" aria-label="Sections">
        {NAV_ITEMS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            className={`cc-nav-item${nav === key ? " on" : ""}`}
            aria-current={nav === key ? "page" : undefined}
            onClick={() => onNav(key)}
            title={collapsed ? label : undefined}
          >
            <span className="cc-nav-ico">
              <Icon size={19} />
            </span>
            <span>{label}</span>
            {key === "notifications" && notifUnread > 0 && (
              <span className="cc-nav-badge" aria-label={`${notifUnread} unread`}>
                {notifUnread > 9 ? "9+" : notifUnread}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="cc-sidebar-spacer" />

      {isOwner && (
        <button
          type="button"
          className={`cc-nav-item${nav === "ops" ? " on" : ""}`}
          aria-current={nav === "ops" ? "page" : undefined}
          onClick={() => onNav("ops")}
          title={collapsed ? "Command Center" : undefined}
        >
          <span className="cc-nav-ico">
            <IconOps size={19} />
          </span>
          <span>Command Center</span>
        </button>
      )}

      {onHelp && (
        <button
          type="button"
          className="cc-nav-item"
          onClick={onHelp}
          title={collapsed ? "Help & FAQ" : undefined}
        >
          <span className="cc-nav-ico">
            <IconHelp size={19} />
          </span>
          <span>Help &amp; FAQ</span>
        </button>
      )}

      <button
        type="button"
        className={`cc-nav-item${nav === "settings" ? " on" : ""}`}
        aria-current={nav === "settings" ? "page" : undefined}
        onClick={() => onNav("settings")}
        title={collapsed ? "Settings" : undefined}
      >
        <span className="cc-nav-ico">
          <IconSettings size={19} />
        </span>
        <span>Settings</span>
      </button>

      <button
        type="button"
        className="cc-collapse"
        onClick={onToggleCollapse}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <span className="cc-nav-ico">
          <IconSidebar size={19} />
        </span>
        <span>Collapse</span>
      </button>

      <div className="cc-ws">{workspaceSlot}</div>
    </aside>
  );
}
